import React, { useState, useEffect } from 'react';
import '@/App.css';
import LandingPage from '@/components/LandingPageClean';
import Dashboard from '@/components/Dashboard';
import AuthPage from '@/components/AuthPage';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

function AppShell() {
  const [currentPage, setCurrentPage] = useState('landing');
  const [authMode, setAuthMode] = useState('signup');
  const { isLoading, isAuthenticated } = useAuth();

  // Hooks must come before any conditional return.
  // After a successful auth, advance from the /auth page to /dashboard automatically.
  useEffect(() => {
    if (isAuthenticated && currentPage === 'auth') {
      setCurrentPage('dashboard');
    }
  }, [isAuthenticated, currentPage]);

  // If the user returns from Stripe (?checkout=success|canceled) and is authed,
  // automatically route to the dashboard so they see confirmation toast + status.
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') && currentPage !== 'dashboard') {
      setCurrentPage('dashboard');
    }
  }, [isAuthenticated, currentPage]);

  // While checking session, show a tiny spinner instead of flashing landing
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Navigation handler
  const navigate = (target) => {
    if (target === 'login') {
      setAuthMode('login');
      setCurrentPage('auth');
      return;
    }
    if (target === 'signup') {
      setAuthMode('signup');
      setCurrentPage('auth');
      return;
    }
    // 'dashboard' just sets the page — the conditional render below
    // gates it on isAuthenticated, so unauthed nav falls through to landing.
    setCurrentPage(target);
  };

  return (
    <div className="App">
      {currentPage === 'landing' && <LandingPage onNavigate={navigate} />}
      {currentPage === 'auth' && <AuthPage onNavigate={navigate} initialMode={authMode} />}
      {currentPage === 'dashboard' && isAuthenticated && <Dashboard onNavigate={navigate} />}
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

export default App;
